import { useSelector } from "react-redux";
import {
  hasPermission,
  hasAtLeastOnePermission,
  hasAllPermissions,
  hasGlobalWildcard,
} from "../_utils/permissions";

/**
 * Hook for checking user permissions in components. Was reimplementing
 * `isAdmin`/`hasWildcardAccess`/`getModulePermissions` inline instead of
 * calling the canonical versions already in `_utils/permissions.js` (which
 * this file already imports the rest of its logic from). `isAdmin` and
 * `getModulePermissions` had zero real callers anywhere in the app -
 * dropped rather than fixed. `hasWildcardAccess` has 19 real callers, so
 * kept, now delegating instead of duplicating.
 * @returns {Object} Object with permission checking functions
 */
export const usePermissionCheck = () => {
  const userPermissions = useSelector((state) => state.auth.userPermissions);
  const permissionBypass = useSelector((state) => state.auth.permissionBypass);
  const permissionsLoaded = useSelector((state) => state.auth.permissionsLoaded);
  const arePermissionsReady = permissionsLoaded || permissionBypass;

  /**
   * Check if user has a specific permission
   * @param {string} permissionKey - Permission key to check
   * @returns {boolean} - True if user has the permission
   */
  const can = (permissionKey) => {
    return hasPermission(userPermissions, permissionKey, permissionBypass);
  };

  /**
   * Check if user has at least one of the specified permissions
   * @param {Array} permissionKeys - Array of permission keys to check
   * @returns {boolean} - True if user has at least one of the permissions
   */
  const canPerformAny = (permissionKeys) => {
    return hasAtLeastOnePermission(userPermissions, permissionKeys, permissionBypass);
  };

  /**
   * Check if user has all of the specified permissions
   * @param {Array} permissionKeys - Array of permission keys to check
   * @returns {boolean} - True if user has all of the permissions
   */
  const canPerformAll = (permissionKeys) => {
    return hasAllPermissions(userPermissions, permissionKeys, permissionBypass);
  };

  /**
   * Check if user has permission for a specific action on a resource
   * @param {string} resource - Resource name (e.g., 'user', 'company')
   * @param {string} action - Action name (e.g., 'create', 'read', 'update', 'delete')
   * @returns {boolean} - True if user has the permission
   */
  const canAction = (resource, action) => {
    const permissionKey = `${resource}.${action}`;
    return hasPermission(userPermissions, permissionKey, permissionBypass);
  };

  /**
   * Check if user has wildcard permissions that grant access to everything
   * @returns {boolean} - True if user has wildcard permissions
   */
  const hasWildcardAccess = () => {
    if (permissionBypass) return true;
    return hasGlobalWildcard(userPermissions);
  };

  return {
    can,
    canPerformAny,
    canPerformAll,
    canAction,
    hasWildcardAccess,
    userPermissions,
    permissionBypass,
    arePermissionsReady,
  };
};
